import { useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useWorkspaceMembers } from '@/features/team/hooks/useWorkspaceMembers'
import { useActiveWorkspace, useSelectionStore } from '@/state/selection.store'
import { useProjectSettingsQuery } from '@/features/settings/hooks/useProjectSettingsQuery'
import { 
  getPermissionsForRole, 
  roleHasPermission, 
  roleHasAnyPermission, 
  roleHasAllPermissions 
} from '@/lib/permissions'
import { safeLocalStorage } from '@/lib/safe-storage'

/** Check if the current user is a platform admin (from localStorage) */
function getIsAdmin() {
  try {
    const raw = safeLocalStorage.getItem('user_data')
    if (!raw) return false
    return JSON.parse(raw).isAdmin === true
  } catch { return false }
}

const VALID_WORKSPACE_ROLES = new Set(['owner', 'admin', 'member', 'viewer'])

function mapProjectRoleToWorkspaceRole(projectRole) {
  if (!projectRole) return null
  
  const normalized = projectRole.toLowerCase()
  if (normalized === 'readonly') return 'viewer'
  if (normalized === 'edit' || normalized === 'editor') return 'member'
  if (VALID_WORKSPACE_ROLES.has(normalized)) return normalized
  
  return null
}

export function usePermissions() {
  const { user } = useAuth()
  const { workspaceId } = useActiveWorkspace()
  const { members = [], loading: membersLoading } = useWorkspaceMembers()
  const [searchParams] = useSearchParams()
  
  const currentProject = useSelectionStore(state => state.currentProject)
  const projects = useSelectionStore(state => state.projects)
  const projectId = searchParams.get('project')
  
  const uniqueUrl = useMemo(() => {
    return currentProject?.uniqueUrl 
      || projects?.find(p => (p.id || p._id) === projectId)?.uniqueUrl 
      || null
  }, [currentProject?.uniqueUrl, projects, projectId])
  
  const { data: projectSettings } = useProjectSettingsQuery(uniqueUrl, {
    enabled: !!uniqueUrl,
  })
  
  const projectRole = useMemo(() => {
    if (!user?.email || !projectSettings?.projectUsers) return null
    const projectUser = projectSettings.projectUsers.find(
      pu => pu.email?.toLowerCase() === user.email?.toLowerCase()
    )
    
    if (projectUser?.role) {
      return mapProjectRoleToWorkspaceRole(projectUser.role)
    }
    
    const ownerEmail = projectSettings?.userOrGuestId?.email
    if (ownerEmail?.toLowerCase() === user.email?.toLowerCase()) {
      return 'owner'
    }
    
    return null
  }, [user?.email, projectSettings?.projectUsers, projectSettings?.userOrGuestId])
  
  const workspaceRole = useMemo(() => {
    // Platform admins get owner-level access to any workspace
    if (getIsAdmin()) return 'owner'
    if (!user?.id || !workspaceId) return null
    
    const userId = user.id
    const member = members.find(m => 
      m.id === userId || 
      m.user?.id === userId ||
      m.user?._id === userId
    )
    
    return member?.role || null
  }, [user?.id, workspaceId, members])
  
  const role = useMemo(() => {
    if (uniqueUrl && projectSettings !== undefined) {
      return projectRole || workspaceRole
    }
    return workspaceRole
  }, [uniqueUrl, projectSettings, projectRole, workspaceRole])
  
  const permissions = useMemo(() => {
    return role ? getPermissionsForRole(role) : []
  }, [role])
  
  const normalizedRole = useMemo(() => role?.toLowerCase() || null, [role])
  
  const hasPermission = useCallback((permission) => {
    if (!role || !permission) return false
    return roleHasPermission(role, permission)
  }, [role])
  
  const hasAnyPermission = useCallback((permissionList) => {
    if (!role || !permissionList?.length) return false
    return roleHasAnyPermission(role, permissionList)
  }, [role])
  
  const hasAllPermissions = useCallback((permissionList) => {
    if (!role || !permissionList?.length) return false
    return roleHasAllPermissions(role, permissionList)
  }, [role])
  
  const hasRole = useCallback((roleToCheck) => {
    if (!normalizedRole) return false
    if (Array.isArray(roleToCheck)) {
      const checkSet = new Set(roleToCheck.map(r => r.toLowerCase()))
      return checkSet.has(normalizedRole)
    }
    return normalizedRole === roleToCheck.toLowerCase()
  }, [normalizedRole])
  
  const isOwnerOrAdmin = useMemo(() => {
    return normalizedRole === 'owner' || normalizedRole === 'admin'
  }, [normalizedRole])
  
  const canWrite = useMemo(() => {
    return normalizedRole === 'owner' || normalizedRole === 'admin' || normalizedRole === 'member'
  }, [normalizedRole])
  
  const isLoading = useMemo(() => {
    return !user || !workspaceId || membersLoading
  }, [user, workspaceId, membersLoading])
  
  return {
    role,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isOwnerOrAdmin,
    canWrite,
    isLoading,
  }
}
